import { Box, Typography } from '@mui/material';
import { Chip } from './ProfileCard';
import type { MeasureHistory } from './accessHistory';
import { ROSTER } from '../tokens';

export const CHART_WIDTH = 176;
export const CHART_HEIGHT = 96;
const BASELINE = 88;
const XS = [22, 66, 110, 154];
const PRIMARY = '#2A2A8B';
const PRIMARY_LIGHT = '#8181D9';
const MARKER = '#C1C1CD';

export function chartY(level: number): number {
  return Math.round((104 - 16 * level) * 10) / 10;
}

function text(value: number | null, missing?: string): string {
  if (value != null) return value.toFixed(1);
  return missing === 'after_exit' ? '·' : '–';
}

const smallSx = { fontFamily: ROSTER.font, fontSize: 16, lineHeight: '24px', color: ROSTER.muted } as const;

export function AccessHistoryChart({ measure, markersAfter, thresholdLabel = 'Exit' }: { measure: MeasureHistory; markersAfter: number[]; thresholdLabel?: 'Exit' | 'Review' }) {
  const markerXs = markersAfter
    .map((after) => measure.slots.findIndex((slot) => slot.schoolYearEnd === after))
    .filter((index) => index >= 0)
    .map((index) => (index < XS.length - 1 ? (XS[index]! + XS[index + 1]!) / 2 : Math.min(XS[index]! + (XS[1]! - XS[0]!) / 2, CHART_WIDTH - 1)));
  const present = measure.slots.map((slot, i) => ({ slot, i })).filter(({ slot }) => slot.value != null);
  const first = measure.slots[0]!.schoolYearEnd;
  const last = measure.slots[measure.slots.length - 1]!.schoolYearEnd;
  return (
    <Box data-testid="access-chart" sx={{ width: CHART_WIDTH, display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box data-testid="chart-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 24 }}>
        <Typography component="b" sx={{ ...smallSx, fontWeight: 600 }}>{measure.label}</Typography>
        {measure.exit != null && <Typography component="span" sx={smallSx}>{`${thresholdLabel} ${measure.exit.toFixed(1)}`}</Typography>}
      </Box>
      <Box data-testid="chart-headline" sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: 32 }}>
        <Typography component="b" sx={{ fontFamily: ROSTER.font, fontSize: 24, lineHeight: '32px', fontWeight: 600, letterSpacing: '-0.5px', color: ROSTER.ink }}>{text(measure.headline)}</Typography>
        {measure.belowExit && <Chip tone="warn">Below exit</Chip>}
      </Box>
      <svg data-testid="chart-svg" width={CHART_WIDTH} height={CHART_HEIGHT} viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} aria-hidden="true" style={{ display: 'block' }}>
        <line x1={0} y1={BASELINE} x2={CHART_WIDTH} y2={BASELINE} stroke={ROSTER.border} />
        {markerXs.map((markerX) => <line key={markerX} data-line="marker" x1={markerX} y1={4} x2={markerX} y2={BASELINE} stroke={MARKER} strokeDasharray="2 3" />)}
        {measure.exit != null && <line data-line="exit" x1={0} y1={chartY(measure.exit)} x2={CHART_WIDTH} y2={chartY(measure.exit)} stroke={ROSTER.muted} strokeDasharray="4 3" />}
        {measure.segments.map((segment) => (
          <line
            key={`${segment.from}-${segment.to}`}
            data-line="segment"
            x1={XS[segment.from]}
            y1={chartY(measure.slots[segment.from]!.value as number)}
            x2={XS[segment.to]}
            y2={chartY(measure.slots[segment.to]!.value as number)}
            stroke={PRIMARY_LIGHT}
            strokeWidth={1.5}
            {...(segment.dotted ? { strokeDasharray: '2 3' } : {})}
          />
        ))}
        {present.map(({ slot, i }, n) => (
          <circle key={slot.schoolYearEnd} cx={XS[i]} cy={chartY(slot.value as number)} r={n === present.length - 1 ? 4.5 : 3} fill={PRIMARY} />
        ))}
      </svg>
      <Box data-testid="chart-values" sx={{ display: 'flex', fontFamily: ROSTER.font, fontSize: 16, lineHeight: '24px', color: ROSTER.ink }}>
        {measure.slots.map((slot) => (
          <Typography key={slot.schoolYearEnd} component="span" sx={{ width: 44, textAlign: 'center', fontFamily: ROSTER.font, fontSize: 16, lineHeight: '24px', color: slot.value == null ? ROSTER.muted : ROSTER.ink }}>
            {text(slot.value, slot.missing)}
          </Typography>
        ))}
      </Box>
      <Box data-testid="chart-years" sx={{ display: 'flex', justifyContent: 'space-between', ...smallSx }}>
        <span>{`${String(first - 1).slice(2)}–${String(first).slice(2)}`}</span>
        <span>{`${String(last - 1).slice(2)}–${String(last).slice(2)}`}</span>
      </Box>
    </Box>
  );
}
